import {
  FC, Children,
} from 'react';
import TabContext from '@mui/lab/TabContext';
import Tabs from '@mui/material/Tabs';
import { styled } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import { motion, AnimatePresence } from 'framer-motion';
import Grid from '@mui/material/Grid2';
import { useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { BASE_COLORS } from '@mgm/common/utils';
import { TBaseTabs, TCustomTab } from './type';

const StyledTab = styled((props: TCustomTab) => (
  <Tab disableRipple {...props} />
))(({ theme }) => ({
  textTransform: 'none',
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: theme.typography.pxToRem(15),
  marginRight: theme.spacing(1),
  minHeight: 0,
  '&.Mui-selected': {
    backgroundColor: BASE_COLORS.primary,
    color: 'white',
    borderRadius: 6,
    minHeight: 40,
    paddingTop: 0.5,
    paddingBottom: 0.5,
  },
}));

const BaseTabs: FC<TBaseTabs> = ({
  value,
  children,
  tabs,
  handleChange,
  centered = true,
  customStyle = {},
  secondaryAction = null,
  isStyledTab = true,
  isHeader = false,
}) => {
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);

  const isMd = useMediaQuery(theme.breakpoints.down('lg'));

  return (
    <TabContext
      value={value}
    >
      <Grid container sx={{ width: '100%' }}>
        <Grid
          marginBottom={{
            xs: 2,
            lg: 0,
            md: 0,
            sm: 0,
          }}
          {...(secondaryAction ? {
            size: {
              xs: 12,
              lg: isHeader ? 7 : 8,
              md: 6,
              sm: isHeader ? 12 : 4,
            },
          } : {
            size: {
              xs: 12,
              lg: 12,
              md: 12,
              sm: 12,
            },
          })}
        >
          <Tabs
            TabIndicatorProps={{
              style: {
                display: isStyledTab ? 'none' : '',
              },
            }}
            allowScrollButtonsMobile
            scrollButtons
            variant={isMobile || (tabs.length > 3 && isMd) ? 'scrollable' : 'standard'}
            value={value}
            onChange={handleChange}
            centered={centered}
            sx={{
              ...customStyle,
            }}
          >
            {tabs.map((tab) => (
              isStyledTab
                ? <StyledTab iconPosition="start" icon={tab.icon} key={tab.value} label={tab.label} value={tab.value} />
                : (
                  <Tab
                    iconPosition="start"
                    icon={tab.icon}
                    key={tab.value}
                    label={tab.label}
                    value={tab.value}
                  />
                )
            ))}
          </Tabs>
        </Grid>
        {secondaryAction && (
        <Grid
          className="display-webkit"
          justifyContent="end"
          size={{
            xs: 12,
            sm: isHeader ? 12 : 8,
            lg: isHeader ? 5 : 4,
            md: 6,
          }}
        >
          {secondaryAction}
        </Grid>
        )}
      </Grid>
      <AnimatePresence>
        <motion.div
          className="TabPanelContainer"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 20 }}
          transition={{ duration: 0.5 }}
        >
          {Children.map(children, (child) => (
            <motion.div
              key={value}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              {child}
            </motion.div>
          ))}
        </motion.div>
      </AnimatePresence>
    </TabContext>
  );
};

export {
  BaseTabs,
};
